// Player token management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a new player token
export const createPlayerToken = async (competitionId, playerId, authUid) => {
  const tokenId = uuidv4();
  const tokenRef = doc(db, 'playerTokens', tokenId);

  await setDoc(tokenRef, {
    competitionId,
    playerId,
    authUid,
    createdAt: serverTimestamp(),
    lastSeenAt: serverTimestamp(),
    deviceLabel: navigator.userAgent.substring(0, 100)
  });

  return tokenId;
};

// Get player token by ID
export const getPlayerToken = async (tokenId) => {
  const tokenRef = doc(db, 'playerTokens', tokenId);
  const tokenSnap = await getDoc(tokenRef);

  if (!tokenSnap.exists()) {
    return null;
  }

  return {
    id: tokenSnap.id,
    ...tokenSnap.data()
  };
};

// Update token's lastSeenAt
export const updateTokenLastSeen = async (tokenId) => {
  const tokenRef = doc(db, 'playerTokens', tokenId);
  await updateDoc(tokenRef, {
    lastSeenAt: serverTimestamp()
  });
};

// Get all tokens for a player
export const getPlayerTokens = async (playerId) => {
  const tokensRef = collection(db, 'playerTokens');
  const q = query(tokensRef, where('playerId', '==', playerId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get all tokens for a competition
export const getCompetitionTokens = async (competitionId) => {
  const tokensRef = collection(db, 'playerTokens');
  const q = query(tokensRef, where('competitionId', '==', competitionId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update token's authUid (used when linking email to anonymous account)
export const updateTokenAuthUid = async (tokenId, newAuthUid) => {
  const tokenRef = doc(db, 'playerTokens', tokenId);
  await updateDoc(tokenRef, {
    authUid: newAuthUid,
    lastSeenAt: serverTimestamp()
  });
};

// Update all tokens for a specific authUid to a new authUid
export const migrateTokensToNewAuthUid = async (oldAuthUid, newAuthUid) => {
  const tokensRef = collection(db, 'playerTokens');
  const q = query(tokensRef, where('authUid', '==', oldAuthUid));
  const querySnapshot = await getDocs(q);

  const updatePromises = querySnapshot.docs.map(docSnapshot => {
    const tokenRef = doc(db, 'playerTokens', docSnapshot.id);
    return updateDoc(tokenRef, {
      authUid: newAuthUid,
      lastSeenAt: serverTimestamp()
    });
  });

  await Promise.all(updatePromises);
  return querySnapshot.docs.length; // Return number of tokens updated
};

// Validate token and check if it's associated with the current auth user
export const validateToken = async (tokenId, authUid) => {
  const token = await getPlayerToken(tokenId);

  if (!token) {
    return { valid: false, reason: 'Token not found' };
  }

  if (token.authUid !== authUid) {
    return { valid: false, reason: 'Token not associated with this device' };
  }

  // Update lastSeenAt
  await updateTokenLastSeen(tokenId);

  return { valid: true, token };
};

// Find tokens for a specific player in a competition
export const findTokensForPlayer = async (competitionId, playerId) => {
  const tokensRef = collection(db, 'playerTokens');
  const q = query(
    tokensRef,
    where('competitionId', '==', competitionId),
    where('playerId', '==', playerId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Check if user has any tokens in the competition with a different authUid
// Returns { hasTokens: boolean, tokens: array, needsMigration: boolean }
export const checkForOrphanedTokens = async (competitionId, playerId, currentAuthUid) => {
  const tokens = await findTokensForPlayer(competitionId, playerId);

  if (tokens.length === 0) {
    return { hasTokens: false, tokens: [], needsMigration: false };
  }

  // Check if any tokens have a different authUid
  const orphanedTokens = tokens.filter(t => t.authUid !== currentAuthUid);
  const needsMigration = orphanedTokens.length > 0;

  return {
    hasTokens: true,
    tokens: orphanedTokens,
    needsMigration
  };
};

// Migrate specific tokens to new authUid
export const migrateSpecificTokens = async (tokenIds, newAuthUid) => {
  const updatePromises = tokenIds.map(tokenId => {
    const tokenRef = doc(db, 'playerTokens', tokenId);
    return updateDoc(tokenRef, {
      authUid: newAuthUid,
      lastSeenAt: serverTimestamp()
    });
  });

  await Promise.all(updatePromises);
  return tokenIds.length;
};
